import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatCompact, formatUsd } from "../lib/renderers.js";
import { pricedPercent } from "../lib/tokenCost.js";
import { cacheReuseRate } from "../lib/tokenVolume.js";
import HoverTooltip from "./HoverTooltip.jsx";
import {
    TRACE_COLUMNS_3,
    TRACE_COLUMNS_4,
    SESSION_COLUMNS_3,
    SESSION_COLUMNS_4,
    unwrapTraceEntry,
    renderTraceCell,
    traceRowClassName,
    traceRowStyle,
} from "./TraceRow.jsx";
import DataTable from "./DataTable.jsx";
import Select from "./Select.jsx";
import QueryBar from "./QueryBar.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import { FUZZY_DIMENSIONS, mergeTimeline, scopeStepLabel, useLabels } from "../lib/dimensionDefs.js";
import { useQuery, useCurrentRange } from "../lib/dataClient.js";
import { parseServerTimestamp } from "../lib/traceUtils.js";
import { modelLabel } from "../lib/agentIdentity.js";
import { fetchSignalBadges } from "../lib/signalsDim.js";


const TRACE_SORT_OPTIONS = [
    ["newest", "Newest first"],
    ["tokens", "Most tokens"],
    ["model", "Model A-Z"],
    ["session", "Session A-Z"],
    ["oldest", "Oldest first"],
];

// Session-grain orderings, as getSessionLog ranks them: by the newest request
// in each session, by request volume, or by the earliest request.
export const SESSION_SORT_OPTIONS = [
    ["newest", "Latest activity"],
    ["oldest", "Oldest first"],
];

// Walk up to the page-level scroll surface (Overview / Endpoint pages
// each declare one) and return the OverlayScrollbars viewport inside
// it. Trace listing virtualization measures scroll against this so the
// existing UX — search/sort/header sticking under the toolbar of the
// outer scroll container — is preserved.
function findTraceScrollRoot(element) {
    if (!element) return null;
    const host =
        element.closest("[data-content-scroll]") ||
        element.closest("[data-endpoint-content-scroll]");
    if (!host) return null;
    return (
        host.querySelector("[data-overlayscrollbars-viewport]") ||
        host.querySelector(".os-viewport") ||
        host
    );
}

// Latches a transient loading flag on for at least `minMs` so users can
// see the indicator even when the worker fetch resolves in <16ms (React
// otherwise batches the false→true→false transition into a single
// render and the flash never paints).
function useStickyLoading(loading, minMs = 220) {
    const [sticky, setSticky] = useState(loading);
    useEffect(() => {
        if (loading) {
            setSticky(true);
            return undefined;
        }
        const t = setTimeout(() => setSticky(false), minMs);
        return () => clearTimeout(t);
    }, [loading, minMs]);
    return sticky;
}

// Adapt a raw analytics candidate row (the per-dim queries pass these
// through unchanged) into the trace shape the rest of the dashboard
// expects. Mirrors the field set the old fuzzyJoinByIds handler used
// when calling adaptTrace; the FE replicates it here so we don't have
// to round-trip through that helper from the per-dim merge.
function rowToTrace(r) {
    const ts = r.event_time ? parseServerTimestamp(r.event_time) : Date.now();
    const sessionShort = r.session_id ? String(r.session_id).slice(0, 8) : "";
    const endpointShort = r.endpoint_id ? String(r.endpoint_id).slice(0, 8) : "";
    const cidNum = r.cluster_id != null ? Number(r.cluster_id) : 0;
    const cidStr = cidNum > 0 ? String(cidNum) : null;
    // skwach tombstone for a captured frame that decoded to no user prompt
    // (metadata/end-marker). Distinguishes "this will never be a prompt" from
    // "not summarised yet", so TraceRow shows "Non-prompt frame" instead of a
    // perpetual "Pending" badge. Carried on analysis_mode; the reduced context
    // projection omits it (as it does message_type), so it stays undefined here.
    const filtered = r.analysisMode === "NON_PROMPT";
    return {
        id: r.trace_id,
        traceId: r.trace_id,
        timestamp: ts,
        filtered,
        // Empty → TraceRow decides whether to show a "Summarising…"
        // pending badge or a muted placeholder based on age.
        summary: r.enriched || "",
        operation: "ai.prompt",
        // Prompt-level request-body model carried through by
        // fuzzyCandidatesByDim — same source the primary list (getTraces)
        // uses (the response-resolved TOKEN_MODEL isn't joined
        // per-prompt on either path). NULL/blank for
        // un-enriched prompts → "unknown" (O-1332). Token counts aren't
        // projected by the per-dim candidate query, so context rows show
        // 0 here; they're context-only and excluded from the summary bar's
        // token total (which sums the primary getTraces rows).
        // modelLabel maps Cursor's empty/"default" sentinel to "Auto" (O-1308).
        model: modelLabel(r.model, r.provider),
        provider: r.provider || "unknown",
        providerApi: "messages.create",
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        endpointId: r.endpoint_id,
        endpointName: endpointShort,
        sessionId: r.session_id,
        sessionTitle: sessionShort || "session",
        sessionStatus: "Live",
        clusterIds: cidStr ? [cidStr] : [],
        primaryClusterId: cidStr,
        primaryClusterName: r.cluster_name || "",
        clusterStatus: filtered ? "filtered" : cidNum > 0 ? "clustered" : cidNum === -1 ? "outlier" : "pending",
        clusterAncestors: [],
        sessionClusters: [],
        excerpt: r.enriched || "",
        settings: { temperature: 0, maxOutputTokens: 0, stream: false, reasoningEffort: "" },
        tools: [],
        spans: [],
        turns: [],
        prevTraceInSession: null,
        apiRequestRaw: "",
    };
}

function withEndpointLabel(trace) {
    const id = trace?.endpointId;
    if (!id) return trace;
    const label = scopeStepLabel({ dim: "endpoints", id });
    if (!label || label === id || label === trace.endpointName) return trace;
    return { ...trace, endpointName: label };
}

/**
 * TraceLogPanel — shared trace log with search, sort, stats bar, header,
 * and a virtualized trace list. Used on both Overview and Endpoint pages.
 *
 * Rows live inside the parent page's scroll container so the page
 * scrolls as a unit (search/sort/stats can scroll out of view if the
 * parent allows it). DataTable is mounted with `findScrollContainer`
 * so its virtualizer hooks the right scroll surface without owning one.
 */
const DEFAULT_ENABLED_DIMS = {
    session: true,
    endpoint: true,
    files: false,
    hosts: false,
    skills: false,
    cluster: false,
    agent: false,
};

// One label/value line in the summary bar's hover breakdown. Emphasis is by weight
// (strong), de-emphasis by color (muted) — never italic (house rule).
function BreakdownRow({ label, value, strong = false }) {
    return (
        <div className="flex items-baseline justify-between gap-[14px]">
            <span className="text-[color:var(--muted)]">{label}</span>
            <span className={strong ? "font-medium text-[color:var(--ink)]" : "text-[color:var(--ink)]"}>{value}</span>
        </div>
    );
}

// The summary bar's cost cell. Three states, and which one shows is a contract
// the cost lib owns (tokenCost.js), not a local styling choice:
//   • priced with usable coverage → the total, WITH its basis stated inline.
//     The three figures to its left are WORK (input+output) while the dollars
//     are priced over THROUGHPUT, and cache reads run orders of magnitude above
//     work on an agentic Anthropic workload — so a bare "6.5M total ≈$2.7K"
//     implies a per-token rate ~550× off. The basis is `pricedThroughput`, not
//     the breakdown's throughput: it is exactly what the total was divided by,
//     so the implied rate stays true even on a partial estimate.
//   • priced but below the coverage floor (`suppressTotal`) → NO dollar figure.
//     A total that omits the model doing the work reads as "spend is
//     negligible" rather than "spend is unknown."
//   • nothing un-priced and nothing priced → nothing.
// Exported for the render-branch tests (TraceLogPanel.tokenCost.test.jsx) —
// same precedent as ContextPanel's MergedMetaBlock. Not used outside this file.
export function CostSummaryCell({ cost }) {
    if (cost?.priced && !cost.suppressTotal) {
        return (
            <span className="text-[color:var(--muted)]">
                ≈<strong className="text-[color:var(--ink)]">{formatUsd(cost.total)}</strong>
                {cost.pricedThroughput > 0 ? ` on ${formatCompact(cost.pricedThroughput)} processed` : ""}
                {cost.hasUnpriced ? ` (${pricedPercent(cost)}% priced)` : ""}
            </span>
        );
    }
    if (cost?.hasUnpriced) {
        return <span className="text-[color:var(--muted)]">{100 - pricedPercent(cost)}% unpriced</span>;
    }
    return null;
}

// Summary-bar token hover card: the full breakdown behind the in·out·total strip.
// Work (input + output) is the headline; "processed" is throughput (cache
// included) so the relationship to the old single number stays visible. Cache
// is Anthropic-only — a zero is shown as an explained note, not "0 / 0" that
// reads as a broken cache. Reasoning shows only where a provider reports it
// (it's already inside output). Coverage is the measured-vs-total honesty
// signal; partial coverage is weighted up because that's the actionable case.
// Exported for the render-branch tests (TraceLogPanel.tokenCost.test.jsx) —
// same precedent as ContextPanel's MergedMetaBlock. Not used outside this file.
export function TokenBreakdownCard({ b, cost = null }) {
    const hasCache = (b.cacheRead || 0) + (b.cacheCreation || 0) > 0;
    const hasReasoning = (b.reasoning || 0) > 0;
    const coverage = b.callsTotal > 0 ? b.callsWithTokens / b.callsTotal : null;
    // Exact (calls are integer counts) — same definition the chip + legend use,
    // so "partial" never disagrees across surfaces over a rounding tolerance.
    const partial = b.callsTotal > 0 && b.callsWithTokens < b.callsTotal;
    const rule = <div className="my-[3px] h-[1px] bg-[color:var(--line-soft)]" aria-hidden="true" />;
    return (
        <div className="grid gap-[3px] min-w-[210px]">
            <div className={EYEBROW_CLASS}>Token volume</div>
            <BreakdownRow label="Input" value={formatCompact(b.input)} />
            <BreakdownRow label="Output" value={formatCompact(b.output)} />
            {rule}
            <BreakdownRow label="Work (in + out)" value={formatCompact(b.work)} strong />
            <BreakdownRow label="Processed (incl. cache)" value={formatCompact(b.throughput)} />
            {rule}
            {hasCache ? (
                <>
                    <BreakdownRow label="Cache read" value={formatCompact(b.cacheRead)} />
                    <BreakdownRow label="Cache write" value={formatCompact(b.cacheCreation)} />
                    {(() => {
                        // Shared "% cached" definition (tokenVolume.js) — matches the chip + legend.
                        const reuse = cacheReuseRate({ input: b.input, cacheRead: b.cacheRead, cacheCreation: b.cacheCreation });
                        return reuse != null
                            ? <BreakdownRow label="Cache reuse" value={`${Math.round(reuse * 100)}%`} strong />
                            : null;
                    })()}
                </>
            ) : (
                <div className="text-[color:var(--muted)]">No prompt-cache traffic (Anthropic only)</div>
            )}
            {hasReasoning && <BreakdownRow label="Reasoning (in output)" value={formatCompact(b.reasoning)} />}
            {coverage != null && (
                <>
                    {rule}
                    <BreakdownRow
                        label="Measured"
                        strong={partial}
                        value={`${formatCompact(b.callsWithTokens)} / ${formatCompact(b.callsTotal)} calls · ${Math.round(coverage * 100)}%`}
                    />
                </>
            )}
            {cost?.priced && !cost.suppressTotal ? (
                <>
                    {rule}
                    <BreakdownRow label="Est. cost" value={formatUsd(cost.total)} strong />
                    {cost.hasUnpriced ? (
                        <div className="text-[color:var(--muted)]">
                            Partial estimate. Priced using {pricedPercent(cost)}% of processed tokens. The rest ran
                            on models with no catalog rate.
                        </div>
                    ) : null}
                </>
            ) : cost?.hasUnpriced ? (
                <>
                    {rule}
                    <BreakdownRow label="Est. cost" value="Not available" strong />
                    <div className="text-[color:var(--muted)]">
                        {100 - pricedPercent(cost)}% of processed tokens ran on models with no catalog
                        rate. There isn’t enough pricing data to estimate a total.
                    </div>
                </>
            ) : null}
        </div>
    );
}

export default function TraceLogPanel({
    traces,
    totalTokens: totalTokensProp,
    tokenBreakdown = null,
    tokenCost = null,
    selectedTraceId = "",
    onSelectTrace = () => {},
    showEndpoint = false,
    title = "Requests",
    searchPlaceholder = "Trace id, summary, session, model, endpoint",
    loading = false,
    // Role-aware denial copy from analyticsErrorCopy() when the getTraces
    // query was denied (403). Shape: { message, ... }. When set, the empty
    // state renders this customer-safe message instead of the misleading
    // "no requests match" copy — and never the raw gRPC status.
    denial = null,
    sort: sortProp,
    onSortChange,
    searchQuery: searchQueryProp,
    onSearchChange,
    // Experimental query-language bar. Rendered only when a surface opts in by
    // passing onQueryChange (ScopedPage); other TraceLogPanel hosts are
    // unaffected. `suggestValues` (optional) powers value autocomplete.
    query = "",
    onQueryChange,
    suggestValues,
    onLoadMore,
    // "requests" lists one row per request; "sessions" lists one row per
    // conversation (getSessionLog rows: trace-shaped, the session's id, the
    // newest request's summary, plus requestCount / firstTimestamp). The
    // table, search, sort and paging are the same surface at either grain,
    // and a row at either grain opens the same trace tab: the whole
    // conversation for a session row, the conversation up to the request for
    // a request row. onSelectTrace(id, row) gets the row as the tab's seed.
    mode = "requests",
    // (grain) when the host lets the reader switch grains from the panel: the
    // summary line then carries a Sessions / Requests chip pair.
    onModeChange = null,
}) {
    const sessionsMode = mode === "sessions";
    const [localSearch, setLocalSearch] = useState("");
    const [localSort, setLocalSort] = useState("newest");
    const sort = sortProp ?? localSort;
    const setSort = onSortChange ?? setLocalSort;
    const searchQuery = searchQueryProp ?? localSearch;
    const setSearchQuery = onSearchChange ?? setLocalSearch;
    const [contextMode, setContextMode] = useState(false);
    const [enabledDims, setEnabledDims] = useState(DEFAULT_ENABLED_DIMS);

    // Signals: a cheap backend secondary pass over the visible rows — read the
    // alert signals each fired from signal_tags (no in-browser scan, no Pyodide).
    // Same data the dedicated signal lenses use, so badges here can't diverge.
    const [signalsMode, setSignalsMode] = useState(false);
    const [signalBadges, setSignalBadges] = useState(() => new Map());

    const TRACE_COLUMNS = sessionsMode
        ? (showEndpoint ? SESSION_COLUMNS_4 : SESSION_COLUMNS_3)
        : (showEndpoint ? TRACE_COLUMNS_4 : TRACE_COLUMNS_3);
    const minWidth = showEndpoint ? 640 : 580;
    const columnHeaderRef = useRef(null);

    // Trace listing isn't user-reconfigurable, so column state lives
    // in-memory (no storageKey). useDataTableColumns still gives us
    // the gridCols string + effective array DataTable expects.
    const cols = useDataTableColumns({
        columns: TRACE_COLUMNS,
        defaultVisible: TRACE_COLUMNS.map((c) => c.id),
    });

    // Server already filtered + sorted; just pass through.
    const visibleTraces = traces;
    const trueTotal =
        typeof traces?.total === "number" ? traces.total : visibleTraces.length;
    const totalIsExact = traces?.totalIsExact !== false;

    // Fuzzy join is split across one anchors hook + one hook per
    // backed dim (session / endpoint / agent). Each per-dim hook has
    // its own loading state so the chips can render their own
    // feedback. Cluster has no backing query — it's applied in the
    // merge step against `anchors.uniqClusters` for any candidate
    // already pulled in by another dim.
    const anchorIds = useMemo(() => visibleTraces.map((t) => t.id), [visibleTraces]);
    const fuzzyRange = useCurrentRange();
    const anchorsQ = useQuery(
        "fuzzyAnchors",
        { anchorIds, range: fuzzyRange },
        { enabled: contextMode && anchorIds.length > 0 },
    );
    const anchors = anchorsQ.data || null;
    const dimQueryEnabled = (dim) =>
        contextMode && Boolean(anchors) && Boolean(enabledDims[dim]);
    const sessionQ = useQuery(
        "fuzzyCandidatesByDim",
        { anchorIds, dim: "session", anchors, range: fuzzyRange },
        { enabled: dimQueryEnabled("session") },
    );
    const endpointQ = useQuery(
        "fuzzyCandidatesByDim",
        { anchorIds, dim: "endpoint", anchors, range: fuzzyRange },
        { enabled: dimQueryEnabled("endpoint") },
    );
    const agentQ = useQuery(
        "fuzzyCandidatesByDim",
        { anchorIds, dim: "agent", anchors, range: fuzzyRange },
        { enabled: dimQueryEnabled("agent") },
    );

    // Per-chip loading state. Sticky each one individually so even
    // sub-frame fetches give a perceptible spinner. Unsupported chips
    // (files / hosts / skills / cluster) have no backing query and
    // never spin.
    const sessionLoading  = useStickyLoading(dimQueryEnabled("session")  && sessionQ.loading);
    const endpointLoading = useStickyLoading(dimQueryEnabled("endpoint") && endpointQ.loading);
    const agentLoading    = useStickyLoading(dimQueryEnabled("agent")    && agentQ.loading);
    const dimLoading = useMemo(
        () => ({
            session: sessionLoading,
            endpoint: endpointLoading,
            agent: agentLoading,
            cluster: false,
            files: false, hosts: false, skills: false,
        }),
        [sessionLoading, endpointLoading, agentLoading],
    );

    // Merge per-dim entries by trace_id, accumulating dimScore + dim
    // labels. Apply the cluster bonus from anchor uniqClusters when
    // the cluster chip is on. Threshold + sort + slice happen here so
    // borderline matches across dims (e.g. 4 + 4) can combine above
    // the cutoff.
    const FUZZY_THRESHOLD = 5;
    const FUZZY_LIMIT = 200;
    const contextTraces = useMemo(() => {
        if (!contextMode || !anchors) return [];
        const merged = new Map();
        const acc = (entries, dim) => {
            if (!Array.isArray(entries)) return;
            for (const e of entries) {
                const id = String(e.trace_id);
                const existing = merged.get(id);
                if (existing) {
                    existing.score += e.dimScore;
                    existing.matches.push(dim);
                } else {
                    merged.set(id, {
                        row: e.row,
                        score: e.dimScore,
                        matches: [dim],
                    });
                }
            }
        };
        if (enabledDims.session)  acc(sessionQ.data, "session");
        if (enabledDims.endpoint) acc(endpointQ.data, "endpoint");
        if (enabledDims.agent)    acc(agentQ.data, "agent");

        if (enabledDims.cluster && anchors.uniqClusters?.length) {
            const clusterSet = new Set(anchors.uniqClusters.map(Number));
            for (const m of merged.values()) {
                const cid = Number(m.row.cluster_id);
                if (cid > 0 && clusterSet.has(cid)) {
                    m.score += 3;
                    m.matches.push("cluster");
                }
            }
        }

        const passing = [];
        for (const m of merged.values()) {
            if (m.score >= FUZZY_THRESHOLD) passing.push(m);
        }
        passing.sort((a, b) => b.score - a.score);
        return passing.slice(0, FUZZY_LIMIT).map(({ row, score, matches }) => ({
            trace: rowToTrace(row),
            score,
            matches,
            isContext: true,
        }));
    }, [
        contextMode,
        anchors,
        enabledDims,
        sessionQ.data,
        endpointQ.data,
        agentQ.data,
    ]);

    const displayTraces = useMemo(() => {
        if (!contextMode) return visibleTraces;
        return mergeTimeline(visibleTraces, contextTraces, sort);
    }, [contextMode, visibleTraces, contextTraces, sort]);

    const endpointLabelSteps = useMemo(() => {
        if (!showEndpoint) return [];
        const seen = new Set();
        const steps = [];
        for (const entry of displayTraces) {
            const { trace } = unwrapTraceEntry(entry, contextMode);
            const id = trace?.endpointId;
            if (!id || seen.has(id)) continue;
            seen.add(id);
            steps.push({ dim: "endpoints", id });
        }
        return steps;
    }, [showEndpoint, displayTraces, contextMode]);
    const endpointLabelTick = useLabels(endpointLabelSteps);

    const labeledDisplayTraces = useMemo(() => {
        if (!showEndpoint) return displayTraces;
        let changed = false;
        const next = displayTraces.map((entry) => {
            const { trace } = unwrapTraceEntry(entry, contextMode);
            const labeled = withEndpointLabel(trace);
            if (labeled === trace) return entry;
            changed = true;
            return contextMode ? { ...entry, trace: labeled } : labeled;
        });
        return changed ? next : displayTraces;
    }, [showEndpoint, displayTraces, contextMode, endpointLabelTick]);

    // The trace list doesn't join per-trace tokens, so the per-row sum is 0.
    // Prefer a scope-level total from the caller (summed once per observation
    // over the TOKEN store, O-2021); fall back to the per-row sum only for
    // callers that still supply per-trace tokens.
    // Guard on Number.isFinite so a malformed prop (e.g. a per-bucket series
    // array, which coerces to NaN through formatCompact) can never render
    // "NaN tokens" in the summary bar — O-1843.
    const totalTokens = useMemo(
        () =>
            Number.isFinite(totalTokensProp)
                ? totalTokensProp
                : visibleTraces.reduce((sum, t) => sum + (t.totalTokens || 0), 0),
        [totalTokensProp, visibleTraces],
    );

    // Per-row signal badges from signal_tags, fetched INCREMENTALLY. A badge is
    // keyed by event_id and stable within a range window, so only a range change
    // (or toggling off) invalidates the cache — sort / search / scope changes
    // just reveal different rows. So we query only the ids we haven't fetched yet
    // and merge, instead of re-querying the whole loaded log every Load More
    // (an event_id IN list that grows without bound and eventually dies).
    const visibleIdsKey = useMemo(() => visibleTraces.map((t) => t.id).join(","), [visibleTraces]);
    const rangeKey = typeof fuzzyRange === "object" ? `${fuzzyRange?.from}-${fuzzyRange?.to}` : String(fuzzyRange ?? "");
    const badgeEpochRef = useRef("");
    const badgeFetchedRef = useRef(new Set());
    useEffect(() => {
        if (!signalsMode || visibleTraces.length === 0) {
            badgeEpochRef.current = "";
            badgeFetchedRef.current = new Set();
            setSignalBadges((prev) => (prev.size ? new Map() : prev));
            return undefined;
        }
        // Range change (or first turn-on) → drop the cache and start the epoch over.
        const epoch = `${signalsMode}|${rangeKey}`;
        if (badgeEpochRef.current !== epoch) {
            badgeEpochRef.current = epoch;
            badgeFetchedRef.current = new Set();
            setSignalBadges((prev) => (prev.size ? new Map() : prev));
        }
        const fetched = badgeFetchedRef.current;
        const newIds = visibleTraces.map((t) => t.id).filter((id) => id && !fetched.has(id));
        if (!newIds.length) return undefined;
        // Mark fetched up front so a re-render mid-flight doesn't re-issue the same
        // ids; a failed fetch just leaves those rows unbadged until the next epoch.
        newIds.forEach((id) => fetched.add(id));
        fetchSignalBadges(newIds, fuzzyRange).then((delta) => {
            // Guard on the epoch (not a per-run flag): every in-flight delta should
            // still land as long as the range hasn't changed under it. Out-of-order
            // deltas merge cleanly via the functional update.
            if (badgeEpochRef.current !== epoch || !delta.size) return;
            setSignalBadges((prev) => {
                const merged = new Map(prev);
                for (const [k, v] of delta) merged.set(k, v);
                return merged;
            });
        });
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalsMode, visibleIdsKey, rangeKey]);

    // Count of CURRENTLY-visible rows that fired ≥1 alert signal (the badge cache
    // can retain entries for rows that scrolled out within the epoch, so count
    // against the visible set rather than the cache size).
    const signalFiredCount = useMemo(
        () => visibleTraces.reduce((n, t) => n + (signalBadges.has(t.id) ? 1 : 0), 0),
        [visibleTraces, signalBadges],
    );
    const signalScanned = visibleTraces.length;

    // Cell renderer / row modifiers — close over `selectedTraceId` and
    // `contextMode` so each row's active / context state is derived
    // from the entry on demand.
    const renderCell = useCallback(
        (entry, col) => {
            const u = unwrapTraceEntry(entry, contextMode);
            const signals = signalBadges.get(u.trace?.id);
            return renderTraceCell(u.trace, col, { ...u, signals });
        },
        [contextMode, signalBadges],
    );

    const rowClassName = useCallback(
        (entry) => {
            const u = unwrapTraceEntry(entry, contextMode);
            const active = u.trace?.id === selectedTraceId;
            return traceRowClassName({ active, isCtx: u.isCtx });
        },
        [contextMode, selectedTraceId],
    );

    const rowStyle = useCallback(
        (entry) => {
            const u = unwrapTraceEntry(entry, contextMode);
            const active = u.trace?.id === selectedTraceId;
            return traceRowStyle({ active, isCtx: u.isCtx, score: u.score });
        },
        [contextMode, selectedTraceId],
    );

    const onRowClick = useCallback(
        (entry) => {
            const { trace } = unwrapTraceEntry(entry, contextMode);
            if (trace?.id) onSelectTrace(trace.id, trace);
        },
        [contextMode, onSelectTrace],
    );

    const getRowId = useCallback(
        (entry) => unwrapTraceEntry(entry, contextMode).trace?.id ?? "",
        [contextMode],
    );

    return (
        <>
            {title ? (
                <div className="grid gap-[2px]">
                    <span className={EYEBROW_CLASS}>Trace log</span>
                    <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em]">
                        {title}
                    </h2>
                </div>
            ) : null}

            <div className="grid gap-[8px]">
                <div
                    className="sticky z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[8px] pb-[8px] shadow-[0_1px_0_var(--line-soft)] max-[900px]:static max-[900px]:shadow-none"
                    style={{ top: 0 }}
                >
                    <div className="grid items-start gap-[8px] [grid-template-columns:minmax(0,1fr)_152px] max-[900px]:grid-cols-1">
                        {onQueryChange ? (
                            // The QueryBar replaces the legacy Search box: bare words
                            // do the same full-text (prompt + provider) match, plus
                            // structured filters. Falls back to plain Search for hosts
                            // that don't opt into the query bar.
                            <QueryBar value={query} onChange={onQueryChange} suggestValues={suggestValues} notes={traces?.queryWarnings} />
                        ) : (
                            <label className="grid gap-[5px]">
                                <span className={NOTE_CLASS}>Search</span>
                                <input
                                    type="search"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    placeholder={searchPlaceholder}
                                    className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                                />
                            </label>
                        )}

                        <label className="grid gap-[5px]">
                            <span className={NOTE_CLASS}>Sort</span>
                            <Select
                                value={sort}
                                onChange={setSort}
                                options={(sessionsMode ? SESSION_SORT_OPTIONS : TRACE_SORT_OPTIONS).map(([value, label]) => ({ value, label }))}
                                ariaLabel={sessionsMode ? "Sort sessions" : "Sort traces"}
                            />
                        </label>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                        <span>
                            <strong className="text-[color:var(--ink)]">{displayTraces.length}</strong>{" "}
                            shown
                        </span>
                        {contextMode && contextTraces.length > 0 && (
                            <span>
                                <strong className="text-[color:var(--ink)]">{contextTraces.length}</strong>{" "}
                                context
                            </span>
                        )}
                        <span>
                            <strong className="text-[color:var(--ink)]">
                                {formatCompact(trueTotal)}
                                {totalIsExact ? "" : "+"}
                            </strong>{" "}
                            {sessionsMode ? "sessions" : "total"}
                        </span>
                        {tokenBreakdown && tokenBreakdown.throughput > 0 ? (
                            <HoverTooltip placement="top" wide label={<TokenBreakdownCard b={tokenBreakdown} cost={tokenCost} />}>
                                <span className="inline-flex items-baseline gap-x-[8px] cursor-help" tabIndex={0}>
                                    <span>
                                        <strong className="text-[color:var(--ink)]">{formatCompact(tokenBreakdown.input)}</strong>{" "}in
                                    </span>
                                    <span>
                                        <strong className="text-[color:var(--ink)]">{formatCompact(tokenBreakdown.output)}</strong>{" "}out
                                    </span>
                                    <span>
                                        <strong className="text-[color:var(--ink)]">{formatCompact(tokenBreakdown.work)}</strong>{" "}total
                                    </span>
                                    <CostSummaryCell cost={tokenCost} />
                                </span>
                            </HoverTooltip>
                        ) : (
                            <span>
                                <strong className="text-[color:var(--ink)]">
                                    {formatCompact(totalTokens)}
                                </strong>{" "}
                                tokens
                            </span>
                        )}
                        {traces?.searchedConversations ? (
                            <span>Conversation text searched across the selected time range. A narrower range returns results faster.</span>
                        ) : null}
                        {/* The chip cluster sits at the line's right edge. The
                            grain pair -- the reader's switch, the one showing
                            lit -- keeps the far right at both grains, so it
                            never moves under the pointer; Context and Signals
                            appear to its left at the request grain, since both
                            read per-REQUEST rows (fuzzy neighbours of a request,
                            the signals a request fired) and a session row is
                            not a request. */}
                        <span className="ml-auto flex items-center gap-[4px]">
                        {sessionsMode ? null : (
                        <button
                            type="button"
                            onClick={() => setContextMode((v) => !v)}
                            className={[
                                "rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
                                contextMode
                                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                                    : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
                            ].join(" ")}
                        >
                            {contextMode ? "Context on" : "Context"}
                        </button>
                        )}
                        {sessionsMode ? null : (
                        <button
                            type="button"
                            onClick={() => setSignalsMode((v) => !v)}
                            className={[
                                "rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
                                signalsMode
                                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                                    : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
                            ].join(" ")}
                        >
                            {signalsMode ? "Signals on" : "Signals"}
                        </button>
                        )}
                        {onModeChange ? (
                        <span className="flex items-center gap-[4px]" data-log-grain="">
                            {[["requests", "Requests"], ["sessions", "Sessions"]].map(([id, label]) => {
                                const on = (id === "sessions") === sessionsMode;
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        data-log-mode={id}
                                        aria-pressed={on}
                                        onClick={() => { if (!on) onModeChange(id); }}
                                        className={[
                                            "rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors",
                                            on
                                                ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium cursor-default"
                                                : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer",
                                        ].join(" ")}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </span>
                        ) : null}
                        </span>
                    </div>

                    {signalsMode && (
                        <div className="flex flex-wrap items-center gap-[4px] pb-[4px]">
                            <span className={NOTE_CLASS}>
                                {`${signalFiredCount} of ${signalScanned} flagged`}
                            </span>
                        </div>
                    )}

                    {contextMode && (
                        <div className="flex flex-wrap items-center gap-[4px] pb-[4px]">
                            {FUZZY_DIMENSIONS.map((dim) => {
                                const on = Boolean(enabledDims[dim.id]);
                                const loading = on && dimLoading[dim.id];
                                return (
                                    <button
                                        key={dim.id}
                                        type="button"
                                        onClick={() =>
                                            setEnabledDims((prev) => ({
                                                ...prev,
                                                [dim.id]: !prev[dim.id],
                                            }))
                                        }
                                        title={dim.description}
                                        className={[
                                            "inline-flex items-center gap-[5px] rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
                                            on
                                                ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                                                : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                                        ].join(" ")}
                                    >
                                        {loading ? (
                                            <svg
                                                className="h-[10px] w-[10px] animate-spin text-[color:var(--green)]"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="8"
                                                    cy="8"
                                                    r="6"
                                                    stroke="currentColor"
                                                    strokeOpacity="0.28"
                                                    strokeWidth="2"
                                                />
                                                <path
                                                    d="M14 8a6 6 0 0 0-6-6"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        ) : null}
                                        {dim.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Standalone column header — pinned with the rest
                        of the toolbar so it stays under the page-level
                        scroll. DataTable's own header is suppressed
                        (showHeader=false) since this one needs to live
                        with the toolbar, not above the rows. */}
                    <div ref={columnHeaderRef} data-trace-log-columns className="overflow-hidden">
                        <div className="will-change-transform" style={{ minWidth: `${minWidth}px` }}>
                            <div
                                className="grid items-center gap-x-2 border-y border-[color:var(--line-soft)] px-[6px] py-[4px] text-[9px] leading-[1.08] tracking-[0.08em] text-[color:var(--muted)] uppercase"
                                style={{ gridTemplateColumns: cols.gridCols }}
                            >
                                {cols.effective.map((c) => (
                                    <span key={c.id}>{c.label}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {labeledDisplayTraces.length ? (
                    <div data-trace-log-scroll className="min-w-0" onScroll={event => {
                        if (columnHeaderRef.current) columnHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
                    }}>
                        <DataTable
                            cols={cols}
                            rows={labeledDisplayTraces}
                            getRowId={getRowId}
                            renderCell={renderCell}
                            loading={loading}
                            showHeader={false}
                            findScrollContainer={findTraceScrollRoot}
                            minWidth={`${minWidth}px`}
                            onRowClick={onRowClick}
                            rowClassName={rowClassName}
                            rowStyle={rowStyle}
                            footer={
                                onLoadMore && visibleTraces.length < trueTotal ? (
                                    <div className="grid place-items-center pt-[8px] pb-[4px]">
                                        <button
                                            type="button"
                                            onClick={onLoadMore}
                                            disabled={loading}
                                            className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                                        >
                                            Load more · {formatCompact(trueTotal - visibleTraces.length)} remaining
                                        </button>
                                    </div>
                                ) : null
                            }
                        />
                    </div>
                ) : loading ? (
                    <div className="grid gap-[6px] py-[12px]">
                        {Array.from({ length: 6 }, (_, i) => (
                            <div
                                key={i}
                                className="h-[32px] rounded-[2px] bg-[color:var(--line-soft)]"
                                style={{ opacity: 0.7 - i * 0.08 }}
                            />
                        ))}
                    </div>
                ) : denial ? (
                    // Analytics denial (a missing permission or a sensitive
                    // read the role lacks). Render the role-aware copy — never
                    // the raw gRPC status string the customer must not see.
                    <div
                        role="alert"
                        className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]"
                    >
                        <strong className="text-[13px] leading-[1.08] font-medium">
                            {denial.message}
                        </strong>
                    </div>
                ) : (
                    <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
                        <strong className="text-[13px] leading-[1.08] font-medium">
                            {sessionsMode ? "No sessions match this search" : "No requests match this search"}
                        </strong>
                        <span className={NOTE_CLASS}>
                            Clear the search or widen the date range to bring {sessionsMode ? "sessions" : "traces"} back into scope.
                        </span>
                    </div>
                )}
            </div>
        </>
    );
}
